/** Work Log — port of the original views.worklog: every Employee Log Work entry, filtered by period, project or member. */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { byDateDesc, fmtH, sum } from '@/domain/format';
import { inRange, periodLabel, periodRange } from '@/domain/periods';
import { useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { ErrorState, LoadingState, PeriodSelect, SearchBox, Topbar, type PeriodValue } from '@/ui/kit';
import { useAppActions } from '../actions';
import { WorkLogTable } from './WorkLogTable';

export function WorkLogPage() {
  const [params] = useSearchParams();
  const d0 = params.get('d') || '';
  // A new ?d= resets the filters, as the original re-rendered the page on every link.
  return <WorkLogView key={d0} d0={d0} />;
}

function WorkLogView({ d0 }: { d0: string }) {
  const ds = useDataset();
  const act = useAppActions();
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState<PeriodValue>(d0 ? { key: 'custom', from: d0, to: d0 } : { key: 'last7', from: '', to: '' });
  const [projectId, setProjectId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [from, to] = periodRange(period.key, period.from, period.to);
  const list = useMemo(() => {
    if (!ds.data) return [];
    const { projects, members, logs } = ds.data;
    const mname = (id: string) => members.find((m) => m.id === id)?.name || '—';
    const pname = (id: string) => (id ? projects.find((p) => p.id === id)?.name || '—' : 'General');
    const s = query.toLowerCase();
    return logs
      .filter(
        (l) =>
          inRange(l.date, [from, to]) &&
          (!projectId || (projectId === 'general' ? !l.projectId : l.projectId === projectId)) &&
          (!memberId || l.memberId === memberId) &&
          (!s || [l.work, mname(l.memberId), pname(l.projectId)].join(' ').toLowerCase().includes(s)),
      )
      .sort((a, b) => byDateDesc(a, b) || mname(a.memberId).localeCompare(mname(b.memberId)));
  }, [ds.data, query, from, to, projectId, memberId]);
  const header = (
    <Topbar title="Work Log" sub="Every Employee Log Work entry — filter by period, project or member">
      <PermissionGate perm="updates:write">
        <button className="btn" onClick={() => act.projectLog()}>
          + Project Log Work
        </button>
      </PermissionGate>
      <PermissionGate perm="worklog:write">
        <button className="btn primary" onClick={() => act.workLog()}>
          + Employee Log Work
        </button>
      </PermissionGate>
    </Topbar>
  );
  if (ds.error) return (<>{header}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data) return (<>{header}<LoadingState /></>);
  const { projects, members } = ds.data;
  const people = new Set(list.map((l) => l.memberId)).size;
  const count = `${list.length} entries · ${people}${people === 1 ? ' person' : ' people'} · ${fmtH(sum(list.map((l) => l.hours)))} in ${periodLabel(period.key, [from, to])}`;
  return (
    <>
      {header}
      <div className="panel">
        <div className="panel-h">
          <div className="row grow">
            <SearchBox value={query} onChange={setQuery} placeholder="Search work done" />
            <PeriodSelect value={period} onChange={setPeriod} />
            <select style={{ width: 'auto' }} value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Project">
              <option value="">All projects</option>
              <option value="general">General</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select style={{ width: 'auto' }} value={memberId} onChange={(e) => setMemberId(e.target.value)} aria-label="Member">
              <option value="">All members</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <span className="hint">{count}</span>
        </div>
        <WorkLogTable logs={list} projects={projects} members={members} />
      </div>
    </>
  );
}
